import React, { useEffect, useState } from 'react';
import useTablePusher from './PusherHandler.js';
import useTakeOrderPusher from './take-order-pusher.js';

/**
 * Dual Pusher Example - Demonstrates using multiple Pusher hooks
 * that share the SAME underlying connection
 * 
 * This example shows how you can use both table-pusher and take-order-pusher
 * hooks in the same component without creating duplicate connections.
 */
const DualPusherExample = () => {
    const [orderNotifications, setOrderNotifications] = useState([]);
    const [tableNotifications, setTableNotifications] = useState([]);

    // Table-specific hook
    const {
        connectionStatus: tableStatus,
        lastUpdate: lastTableUpdate,
        addEventHandler: addTableHandler,
        isConnected: tableConnected
    } = useTablePusher();

    // Order-specific hook (uses SAME connection)
    const {
        connectionStatus: orderStatus,
        lastOrderUpdate,
        addOrderEventHandler,
        isConnected: orderConnected,
        sendOrderMessage
    } = useTakeOrderPusher();

    // Set up table event handlers
    useEffect(() => {
        addTableHandler('booking_created', (data) => {
            console.log('Table booking created:', data);
            setTableNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'table-event',
                message: data.message || 'Table event received',
                timestamp: new Date().toLocaleTimeString()
            }]);
        });

        addTableHandler('booking_updated', (data) => {
            console.log('Table booking updated:', data);
            setTableNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'table-event',
                message: data.message || 'Table event received',
                timestamp: new Date().toLocaleTimeString()
            }]);
        });


        addTableHandler('table_status_updated', (data) => {
            console.log('Table status update:', data);
            setTableNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'table-event',
                message: data.message || 'Table event received',
                timestamp: new Date().toLocaleTimeString()
            }]);
        });

        addTableHandler('reservation_updated', (data) => {
            setTableNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'table-event',
                message: data.message || 'Table event received',
                timestamp: new Date().toLocaleTimeString()
            }]);
        });
        
                addTableHandler('reservation_updated', (data) => {
            setTableNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'table-event',
                message: data.message || 'Table event received',
                timestamp: new Date().toLocaleTimeString()
            }]);
        });
    }, [addTableHandler]);

    // Set up order event handlers
    useEffect(() => {
        addOrderEventHandler('order-completed', (data) => {
            setOrderNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'order-completed',
                message: `Order #${data.orderId || 'Unknown'} completed`,
                timestamp: new Date().toLocaleTimeString()
            }]);
        });

        addOrderEventHandler('order-cancelled', (data) => {
            setOrderNotifications(prev => [...prev, {
                id: Date.now(),
                type: 'order-cancelled',
                message: `Order #${data.orderId || 'Unknown'} cancelled`,
                timestamp: new Date().toLocaleTimeString()
            }]);
        });
    }, [addOrderEventHandler]);

    // Handle table updates
    useEffect(() => {
        if (lastTableUpdate) {
            console.log('Table update received:', lastTableUpdate);
        }
    }, [lastTableUpdate]);

    // Handle order updates
    useEffect(() => {
        if (lastOrderUpdate) {
            console.log('Order update received:', lastOrderUpdate);
        }
    }, [lastOrderUpdate]);

    const sendTestOrderMessage = () => {
        const success = sendOrderMessage('test-order-event', {
            orderId: Math.floor(Math.random() * 1000),
            message: 'Test order message',
            timestamp: Date.now()
        });
        
        if (!success) {
            alert('Failed to send order message. Make sure you are connected.');
        }
    };

    const clearOrderNotifications = () => setOrderNotifications([]);
    const clearTableNotifications = () => setTableNotifications([]);

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Dual Pusher Example</h1>
            
            {/* Connection Status Comparison */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Table Connection Status */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h2 className="text-lg font-semibold mb-3 text-blue-600">Table Pusher</h2>
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">Status:</span>
                            <span className={`font-semibold ${tableConnected ? 'text-green-500' : 'text-red-500'}`}>
                                {tableStatus.toUpperCase()}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            Channel: Table updates & notifications
                        </div>
                    </div>
                </div>

                {/* Order Connection Status */}
                <div className="bg-white rounded-lg shadow p-4">
                    <h2 className="text-lg font-semibold mb-3 text-purple-600">Order Pusher</h2>
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">Status:</span>
                            <span className={`font-semibold ${orderConnected ? 'text-green-500' : 'text-red-500'}`}>
                                {orderStatus.toUpperCase()}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            Channel: Order updates & kitchen status
                        </div>
                    </div>
                </div>
            </div>

            {/* Shared Connection Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-green-800 mb-2">✅ Single Connection Shared</h3>
                <div className="text-sm text-green-700">
                    <p>Both hooks use the SAME WebSocket connection!</p>
                    <p>Status should be identical: Table ({tableStatus}) = Order ({orderStatus})</p>
                    <p>🔗 Connection efficiency: Both channels through one socket</p>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 mb-6">
                <button
                    onClick={sendTestOrderMessage}
                    disabled={!orderConnected}
                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    Send Test Order Event
                </button>
            </div>

            {/* Notifications Display */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Table Notifications */}
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-blue-600">Table Events</h2>
                        <button
                            onClick={clearTableNotifications}
                            className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                            Clear
                        </button>
                    </div>
                    
                    {tableNotifications.length === 0 ? (
                        <p className="text-gray-500 text-sm">No table events received yet.</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {tableNotifications.map((notification) => (
                                <div key={notification.id} className="p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                                    <div className="text-sm font-medium text-blue-800">
                                        {notification.message}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {notification.timestamp} • Type: {notification.type}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Order Notifications */}
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-purple-600">Order Events</h2>
                        <button
                            onClick={clearOrderNotifications}
                            className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                            Clear
                        </button>
                    </div>
                    
                    {orderNotifications.length === 0 ? (
                        <p className="text-gray-500 text-sm">No order events received yet.</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {orderNotifications.map((notification) => (
                                <div key={notification.id} className="p-3 bg-purple-50 rounded border-l-4 border-purple-500">
                                    <div className="text-sm font-medium text-purple-800">
                                        {notification.message}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {notification.timestamp} • Type: {notification.type}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Last Updates Display */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
                {/* Last Table Update */}
                {lastTableUpdate && (
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-semibold text-blue-600 mb-3">Last Table Update</h3>
                        <div className="bg-gray-50 rounded p-3">
                            <div className="text-sm text-gray-600">
                                <strong>Type:</strong> {lastTableUpdate.type}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                                <strong>Time:</strong> {new Date(lastTableUpdate.timestamp).toLocaleString()}
                            </div>
                            <pre className="text-xs bg-gray-100 p-2 rounded mt-2 overflow-x-auto">
                                {JSON.stringify(lastTableUpdate.data, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}

                {/* Last Order Update */}
                {lastOrderUpdate && (
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-semibold text-purple-600 mb-3">Last Order Update</h3>
                        <div className="bg-gray-50 rounded p-3">
                            <div className="text-sm text-gray-600">
                                <strong>Type:</strong> {lastOrderUpdate.type}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                                <strong>Time:</strong> {new Date(lastOrderUpdate.timestamp).toLocaleString()}
                            </div>
                            <pre className="text-xs bg-gray-100 p-2 rounded mt-2 overflow-x-auto">
                                {JSON.stringify(lastOrderUpdate.data, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}
            </div>

            {/* Technical Info */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Technical Details</h3>
                <div className="text-sm text-gray-700 space-y-1">
                    <p>🔗 <strong>Single WebSocket:</strong> Both hooks share one Pusher connection</p>
                    <p>📡 <strong>Multiple Channels:</strong> Different channels for tables and orders</p>
                    <p>⚡ <strong>Efficient:</strong> No duplicate connections or authentication</p>
                    <p>🔄 <strong>Real-time:</strong> Instant updates across all channels</p>
                    <p>💾 <strong>Resource Friendly:</strong> Minimal memory and network usage</p>
                </div>
            </div>
        </div>
    );
};

export default DualPusherExample;